/**
 * Shared validation for the positive-amount fields on the coupon and offer
 * forms (minimum order value, maximum discount, discount, coupon limit).
 *
 * These used to be checked with a bare `!fields[name]` truthiness test, which
 * only catches an empty box - a negative like -50 passed validation with no
 * message shown and was posted to the API. The COD minimum order value on the
 * retailer form already guarded against that; these helpers are that same
 * rule, in one place, so the two forms cannot drift apart again.
 */

//  Digits with at most two decimal places. Deliberately does not accept a
//  leading "-" or "+", so a pasted or spinner-produced negative fails here
//  rather than reaching the server.
const AMOUNT_PATTERN = /^\d+(\.\d{1,2})?$/;
const WHOLE_NUMBER_PATTERN = /^\d+$/;

/**
 * @param {*} value        raw field value
 * @param {string} label   field name as it reads on screen, used in messages
 * @param {object} options allowDecimals - false for counts like coupon limit
 * @returns {string} the error message, or "" when the value is acceptable
 */
export const validatePositiveAmount = (value, label, options = {}) => {
  const { allowDecimals = true } = options;
  const trimmed = value == null ? "" : String(value).trim();

  if (!trimmed) {
    return `${label} cannot be empty`;
  }

  const pattern = allowDecimals ? AMOUNT_PATTERN : WHOLE_NUMBER_PATTERN;

  if (!pattern.test(trimmed)) {
    return allowDecimals
      ? `Enter a valid ${label.toLowerCase()} (positive number up to 2 decimals)`
      : `Enter a valid ${label.toLowerCase()} (positive whole number)`;
  }

  if (Number(trimmed) <= 0) {
    return `${label} must be greater than 0`;
  }

  return "";
};

/**
 * Keydown guard for the same inputs. type="number" still lets the user type a
 * minus sign or exponent notation, so those keys are dropped before they
 * reach the field - the validation above is the backstop for pasted values.
 */
export const blockNegativeAmountKeys = (event) => {
  if (["-", "+", "e", "E"].includes(event.key)) {
    event.preventDefault();
  }
};
